/**
 * Slider de rango para el filtro de precio.
 *
 * alessi.com resuelve el precio con dos campos numericos arriba y una linea de 1px
 * abajo con dos manijas redondas. Horizon solo trae los campos, asi que este script
 * agrega el slider encima de lo que ya existe.
 *
 * Se monta por JS y no desde Liquid a proposito: si algo falla —no hay rango, no se
 * puede parsear el maximo, no carga el modulo— no se dibuja nada y los campos
 * numericos siguen funcionando exactamente como hoy. El filtro nunca queda peor que
 * antes por culpa del slider.
 *
 * No inventa logica de filtrado: escribe en los mismos inputs que ya existen y
 * dispara un `change`, que es lo que <price-facet-component> escucha para actualizar
 * los resultados. Y formatea con el mismo helper del theme que usa facets.js, para
 * que el valor escrito sea indistinguible del que escribe el propio theme.
 */
import { formatMoney, convertMoneyToMinorUnits } from '@theme/money-formatting';

/** Saca el placeholder del formato de moneda, igual que hace facets.js. */
const placeholderOf = (format) => (String(format).match(/{{\s*\w+\s*}}/) || ['{{amount}}'])[0];

const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), hi);

function mount(component) {
  if (component.dataset.rangeSliderReady === 'true') return;

  const minInput = component.querySelector('[ref="minInput"]');
  const maxInput = component.querySelector('[ref="maxInput"]');
  const anchor = component.querySelector('.facets__inputs-wrapper');
  if (!minInput || !maxInput || !anchor) return;

  const currency = component.dataset.currency || 'USD';
  const format = placeholderOf(component.dataset.moneyFormat || '{{amount}}');

  // El techo sale de un atributo propio y no del data-max del input: ese ultimo se
  // achica cuando hay un filtro de precio activo, y el slider tiene que seguir
  // representando el rango completo de la coleccion.
  const ceiling = convertMoneyToMinorUnits(component.dataset.rangeMax || '', currency);
  if (!ceiling || !Number.isFinite(ceiling) || ceiling <= 0) return;

  // Paso de una unidad mayor (un peso), no de un centavo.
  const step = convertMoneyToMinorUnits('1', currency) || 1;

  const read = (input, fallback) => {
    const v = convertMoneyToMinorUnits(input.value, currency);
    return v == null || !Number.isFinite(v) ? fallback : clamp(v, 0, ceiling);
  };

  const slider = document.createElement('div');
  slider.className = 'price-facet__slider';
  slider.innerHTML =
    '<span class="price-facet__slider-rail"></span>' +
    '<span class="price-facet__slider-fill"></span>';

  const makeRange = (kind, value, label) => {
    const el = document.createElement('input');
    el.type = 'range';
    el.className = `price-facet__slider-input price-facet__slider-input--${kind}`;
    el.min = '0';
    el.max = String(ceiling);
    el.step = String(step);
    el.value = String(value);
    if (label) el.setAttribute('aria-label', label);
    return el;
  };

  const lo = makeRange('min', read(minInput, 0), minInput.getAttribute('aria-label'));
  const hi = makeRange('max', read(maxInput, ceiling), maxInput.getAttribute('aria-label'));
  slider.append(lo, hi);
  anchor.insertAdjacentElement('afterend', slider);

  const fill = slider.querySelector('.price-facet__slider-fill');

  const paint = () => {
    const a = (Number(lo.value) / ceiling) * 100;
    const b = (Number(hi.value) / ceiling) * 100;
    fill.style.insetInlineStart = `${a}%`;
    fill.style.width = `${Math.max(0, b - a)}%`;
    // La manija que este mas cerca del cursor tiene que poder agarrarse: cuando las
    // dos estan en el mismo punto, la de arriba taparia a la otra para siempre.
    lo.style.zIndex = Number(lo.value) > ceiling - step ? '4' : '3';
  };

  /** Escribe en el campo numerico con el mismo formato que usa el theme. */
  const write = (input, cents) => {
    input.value = formatMoney(cents, format, currency);
  };

  const onSlide = (moved) => {
    let a = Number(lo.value);
    let b = Number(hi.value);
    // Las manijas no se cruzan: la que se movio empuja, la otra no cede.
    if (a > b) {
      if (moved === lo) a = b;
      else b = a;
      lo.value = String(a);
      hi.value = String(b);
    }
    write(minInput, a);
    write(maxInput, b);
    paint();
  };

  const commit = () => {
    // El componente del theme escucha `change` y recalcula resultados. Lo emitimos
    // desde el input numerico, que es el que su handler lee.
    maxInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  for (const el of [lo, hi]) {
    el.addEventListener('input', () => onSlide(el));
    el.addEventListener('change', commit);
  }

  // Si el usuario escribe en los campos, las manijas acompanan.
  for (const el of [minInput, maxInput]) {
    el.addEventListener('change', () => {
      lo.value = String(read(minInput, 0));
      hi.value = String(read(maxInput, ceiling));
      paint();
    });
  }

  paint();
  component.dataset.rangeSliderReady = 'true';
}

const mountAll = (root = document) => {
  for (const c of root.querySelectorAll('price-facet-component[data-range-max]')) mount(c);
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => mountAll());
} else {
  mountAll();
}

// Los facets se re-renderizan al filtrar: hay que volver a montar sobre el nodo nuevo.
new MutationObserver((records) => {
  for (const r of records) {
    for (const node of r.addedNodes) {
      if (node.nodeType !== 1) continue;
      if (node.matches?.('price-facet-component[data-range-max]')) mount(node);
      else mountAll(node);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
